/**
 * Legible ink for website theme colours.
 *
 * The palette is a 3-tuple (primary / secondary / background) and has no
 * subtext slot, so `deriveThemeFromPalette` used to paint body copy, form
 * labels and ghost-CTA labels in `palette.secondary`. In the 13 website
 * presets `secondary` is a DECORATIVE ACCENT — soft green, antique gold,
 * indigo — not a text colour, so the derivation replaced each preset's
 * carefully chosen ink with one that fails WCAG: charity's body copy went
 * from 15.02:1 to 1.51:1, corporate to 1.97:1, dinner to 2.19:1.
 * `corporate.ts` even documents its own accent as "fails on light marble
 * (1.97:1)" — and the derivation then used it for every paragraph.
 *
 * The rule here is a three-rung chain: keep the authored ink while it is
 * legible on the surface it will actually sit on, fall back to the palette's
 * secondary while THAT is legible (which preserves the existing behaviour of
 * every palette tile and the light/dark toggle — all 30 wizard palettes carry
 * a legible secondary), and only then compute a muted ink from the surface.
 *
 * Deliberately NOT applied to `formInputTheme.borderColor`: a hairline is not
 * text, and every preset authors a sub-3:1 rule on purpose (charity 1.40:1,
 * networking 1.20:1). Any contrast gate would destroy them.
 */
import {
  blend,
  contrastRatio,
  ensureContrast,
  isDarkColor,
  DEVICE_INK_LIGHT,
  DEVICE_INK_DARK,
} from "@/pages/builders/app/appThemePalette";
import { parseCssColor } from "./color-value";

/** WCAG AA for body text. */
export const MIN_INK_CONTRAST = 4.5;

/**
 * Muting factor for the computed fallback. Matches the house convention
 * already used for body copy on the device (`inkSecondary: rgba(ink, 0.72)`),
 * so a computed website ink and a computed app ink read the same weight.
 */
const MUTE = 0.72;

const toHex2 = (n: number) =>
  Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, "0");

/**
 * Normalise any storable colour to an opaque `#RRGGBB` over the given surface.
 *
 * Two jobs at once. It flattens alpha — `productLaunch` authors its body ink as
 * `rgba(255,255,255,0.72)`, and the backend's colour columns are
 * `^(#[0-9A-Fa-f]{6,8}|transparent|inherit)$`, so persisting the raw value
 * would 400 the create. And it gives the contrast maths a real colour to
 * measure, which `parseHex` alone cannot do for an `rgba()` string.
 *
 * Returns `undefined` for anything unparseable, which callers must read as
 * "nothing authored here" — never as "substitute a default".
 */
export function flattenInk(
  value: string | undefined | null,
  overBgHex: string,
): string | undefined {
  const c = parseCssColor(value);
  if (!c) return undefined;
  // Fully opaque: hand the original string back untouched. Re-emitting a
  // re-cased hex would churn `pickDeep`'s identity test for no reason.
  if (c.a >= 1) return typeof value === "string" ? value : undefined;
  const bg = parseCssColor(overBgHex) ?? { r: 255, g: 255, b: 255, a: 1 };
  return (
    "#" +
    toHex2(c.r * c.a + bg.r * (1 - c.a)) +
    toHex2(c.g * c.a + bg.g * (1 - c.a)) +
    toHex2(c.b * c.a + bg.b * (1 - c.a))
  ).toUpperCase();
}

const mutedCache = new Map<string, string>();

/**
 * A muted, legible ink for `bgHex`: the polarity ink at 72%, raised to AA by
 * `ensureContrast` if the mute took it under.
 *
 * Measured on the real backgrounds this sees — `#FFFFFF` → `#545963` (7.03:1),
 * `#F5F0E8` → `#51545D` (6.67:1), `#0A0A0A` → `#BABABA` (10.20:1). Body-copy
 * greys, not slabs. On hostile mid-tones (`#808080`, `#2563EB`) the
 * `ensureContrast` gate rescues to a full-strength polarity ink.
 */
export function mutedInkOn(bgHex: string | undefined | null): string {
  // Callers can hand this a partial palette — the runtime twin crashed a whole
  // published page on exactly that (`palette.background` undefined, the old
  // `?? palette.secondary` had tolerated it). Treat an unusable surface as
  // white, which is what relLuminance already assumes for unparseable input.
  const key = (typeof bgHex === "string" && bgHex.trim() !== "" ? bgHex : "#FFFFFF")
    .trim()
    .toLowerCase();
  const hit = mutedCache.get(key);
  if (hit) return hit;
  const polarity = isDarkColor(key) ? DEVICE_INK_DARK : DEVICE_INK_LIGHT;
  let out = ensureContrast(blend(polarity, MUTE, key), key, MIN_INK_CONTRAST);
  if (contrastRatio(out, key) < MIN_INK_CONTRAST) {
    // `ensureContrast`'s polarity inks are the DEVICE defaults, and #111827 is
    // not quite black — on a mid grey (#808080) it reaches only 4.49:1, so the
    // helper returns "whichever is closer" and lands just under AA. Pure black
    // or white always clears it: no surface exists where both fail, since that
    // would need luminance < 0.175 and > 0.183 at once.
    out = contrastRatio("#000000", key) >= contrastRatio("#FFFFFF", key)
      ? "#000000"
      : "#FFFFFF";
  }
  // Bounded like appThemePalette's own memo — this is a render-path helper.
  if (mutedCache.size > 64) mutedCache.clear();
  mutedCache.set(key, out);
  return out;
}

/**
 * The ink to use on `surfaceHex`, given what the theme currently holds.
 *
 * Rung 1 keeps the authored value while it is legible — this is what restores
 * every preset's own ink. Rung 2 keeps today's `palette.secondary` behaviour
 * while that is legible, so palette tiles and the light/dark toggle still move
 * body copy the way they always have. Rung 3 computes.
 *
 * Pure: a function of its arguments only, which is what lets
 * `deriveThemeFromPalette` stay comparable across the old and new palette in
 * `syncThemeFromPalette`'s three-argument lane.
 */
export function inkOnSurface(
  authored: string | undefined | null,
  surfaceHex: string,
  paletteSecondary: string,
): string {
  const flat = flattenInk(authored, surfaceHex);
  if (flat && contrastRatio(flat, surfaceHex) >= MIN_INK_CONTRAST) return flat;
  if (contrastRatio(paletteSecondary, surfaceHex) >= MIN_INK_CONTRAST) return paletteSecondary;
  return mutedInkOn(surfaceHex);
}

/**
 * The surface a piece of text actually sits on. Twin of the runtime copy —
 * keep both in step.
 *
 * A form LABEL sits on the page; the value inside the input sits on the input's
 * own fill. A fill that is `transparent`, `inherit`, empty or non-hex shows the
 * page through, so the page is the honest surface.
 */
export const surfaceOrPage = (fill: string | undefined | null, page: string): string => {
  if (typeof fill !== "string") return page;
  const f = fill.trim();
  if (!/^#[0-9a-fA-F]{3,8}$/.test(f)) return page;
  return f;
};
